/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  GET_APPLICATION_MENU,
  CHECK_APPLICATION_STATUS,
  FETCH_EMPLOYEES,
  EMPLOYEES_LOADED,
  START_INITIAL_SYNC,
  COMPANY_PROFILE_LOADED,
  receiveApplicationMenu,
  receiveApplicationStatus,
  receiveAccountId,
  replaceEmployees,
  DELETE_EMPLOYEE,
  DELETING_EMPLOYEE,
  DELETED_EMPLOYEE,
  INITIAL_STATE_LOADED,
} from "EmployerApplication/Actions";
import { api } from "EmployerApplication/urls";
import { getAccount, getCompanyApplicationByCompanyProfileId, getCompanyPolicyById, getCompanyProfileById } from "EntitiesApi/api";
import { tryGetCompanyPolicyById as selectCompanyPolicyById, tryGetCompanyProfileById as selectCompanyProfileById } from "EntitiesApi/Selectors";
import isIntuit from "Intuit/isIntuit";
import get from "lodash/get";
import {
  DENTAL_PLANS_REQUESTED,
  VISION_PLANS_REQUESTED,
  UPDATE_DENTAL_PLANS,
  UPDATE_VISION_PLANS,
  requestDentalPlans,
  requestVisionPlans,
  INITIAL_STATE_LOAD_SUCCESS,
} from "QuotePage/Actions";
import { delay, takeLatest, takeEvery } from "redux-saga";
import { fork, call, put, select, take } from "redux-saga/effects";
import isSquare from "Square/isSquare";
import { replaceState } from "Utils/allowReplacement";
import companyToken from "Utils/companyToken";
import fetchWithCSRFToken from "Utils/fetchWithCSRFToken";
import { defaultHandler, ForbiddenError } from "Utils/http";

/**
  Unified initial state loader that detects renewal vs. non-renewal flow
  and loads the appropriate data accordingly.
*/

export function* watchGetApplicationsMenuState() {
  yield takeLatest(GET_APPLICATION_MENU, getApplicationMenuState);
}

function* getApplicationMenuState() {
  const queryString = require("query-string");
  const parsed = queryString.parse(window.location.search);
  const companyIDToken = parsed.company_id_token || companyToken();
  const response = yield call(applicationMenuStateRequest, companyIDToken);
  const action = receiveApplicationMenu(response.applicationMenu);
  yield put(action);
}

function applicationMenuStateRequest(companyIDToken) {
  const urlParamsObj = {
    company_id_token: companyIDToken,
  };

  const urlParamsString = Object.keys(urlParamsObj)
    .filter((key) => urlParamsObj[key])
    .map((key) => `${key}=${urlParamsObj[key]}`)
    .join("&");
  const urlParams = urlParamsString ? `?${urlParamsString}` : "";
  const url = `${api.documentsMenuState}${urlParams}`;
  return fetchWithCSRFToken(url, {
    method: "get",
  }).then((response) => response.json());
}

export function* watchGetApplicationStatus() {
  yield takeLatest(CHECK_APPLICATION_STATUS, checkApplicationStatus);
}

function* checkApplicationStatus() {
  const queryString = require("query-string");
  const parsed = queryString.parse(window.location.search);
  const companyIDToken = parsed.company_id_token || companyToken();
  const response = yield call(applicationStatusRequest, companyIDToken);
  const action = receiveApplicationStatus(response.applicationStatus);
  yield put(action);
}

function applicationStatusRequest(companyIDToken) {
  const urlParamsObj = {
    company_id_token: companyIDToken,
  };

  const urlParamsString = Object.keys(urlParamsObj)
    .filter((key) => urlParamsObj[key])
    .map((key) => `${key}=${urlParamsObj[key]}`)
    .join("&");
  const urlParams = urlParamsString ? `?${urlParamsString}` : "";
  const url = `${api.checkApplicationStatus}${urlParams}`;
  return fetchWithCSRFToken(url, {
    method: "get",
  }).then((response) => response.json());
}

function fetchEmployeeApi(forceSync, companyIDToken) {
  const urlParamsObj = {
    full_sync: true,
    force_sync: forceSync,
    company_id_token: companyIDToken,
  };

  const urlParamsString = Object.keys(urlParamsObj)
    .filter((key) => urlParamsObj[key])
    .map((key) => `${key}=${urlParamsObj[key]}`)
    .join("&");
  const urlParams = urlParamsString ? `?${urlParamsString}` : "";
  const url = `${api.companyProfile}${urlParams}`;
  return fetchWithCSRFToken(url, {
    method: "GET",
    processData: false,
  })
    .then((response) => {
      if (response.status === 401) {
        throw new Error("Logging in");
      } else if (response.status === 400) {
        return Promise.reject(response);
      } else {
        return response;
      }
    })
    .then((response) => response.json());
}

/*
 * END - Alternative Shopping Cart AB Test methods
 */

function* fetchCompanyProfileApi(fullSync, companyIDToken) {
  const isRenewal = yield select((state) => {
    const { renewalPolicy, companyPolicyId } = state;
    return renewalPolicy && renewalPolicy.id === companyPolicyId;
  });

  const urlParamsObj = {
    full_sync: fullSync,
    company_id_token: companyIDToken,
  };

  const urlParamsString = Object.keys(urlParamsObj)
    .filter((key) => urlParamsObj[key])
    .map((key) => `${key}=${urlParamsObj[key]}`)
    .join("&");
  const url = `${api.companyProfile}?${urlParamsString}`;

  let response;
  try {
    response = yield call(fetchWithCSRFToken, url, {
      method: "GET",
      processData: false,
    });
  } catch (e) {
    yield call(fetchInitialDataFailure, e);
    return;
  }

  if (response.status === 401) {
    throw new Error("Logging in");
  } else if (response.status === 400) {
    yield call(fetchInitialDataFailure, response);
    return;
  }

  const parsedResponse = yield call([response, response.json]);

  return isRenewal ? yield call(fetchRenewalDataSuccess, parsedResponse) : yield call(fetchInitialDataSuccess, parsedResponse);
}

function fetchInitialDataSuccess(response) {
  const json = response;
  const {
    company_profile: { quickbooksCompanyId },
  } = json;

  if (quickbooksCompanyId) {
    window.quickbooksCompanyId = quickbooksCompanyId;
  }

  const result = {
    asyncInitialState: { loading: false, loaded: true },
  };

  if (!isIntuit() && !isSquare()) {
    result.employees = response.employees;
  }

  return result;
}

function fetchRenewalDataSuccess(response) {
  const {
    cart,
    company_profile,
    employee_profiles,
    carrier_deadline_options,
    alternative_carriers_for_coverage_start_date,
    alternative_carriers_for_owner_enrollment,
    allows_declining_ee,
    ab_tests,
    siteConfig,
    employees,
  } = response;

  if (company_profile.quickbooksCompanyId) {
    window.quickbooksCompanyId = company_profile.quickbooksCompanyId;
  }

  const result = {
    abTests: ab_tests,
    alternativeCarriersForCoverageStartDate: alternative_carriers_for_coverage_start_date,
    alternativeCarriersForOwnerEnrollment: alternative_carriers_for_owner_enrollment,
    allowsDecliningEmployees: allows_declining_ee,
    carrierDeadlines: carrier_deadline_options,
    cart,
    companyProfile: company_profile,
    siteConfig,
    asyncInitialState: { loading: false, loaded: true },
    employeeProfile: employee_profiles,
  };

  if (!isIntuit() && !isSquare()) {
    result.employees = employees;
  }

  return result;
}

function fetchInitialDataFailure(errorReponse) {
  if (errorReponse instanceof Error) throw errorReponse;
  return errorReponse.json().then((errorJson) => {
    throw new Error("Unsupported 400 response handler.");
  });
}

export function* watchLoadInitialState() {
  yield takeLatest("REQUEST_ER_APP_INITIAL_LOAD", function* () {
    yield take(INITIAL_STATE_LOAD_SUCCESS);

    const isRenewal = yield select((state) => {
      const { renewalPolicy, companyPolicyId } = state;
      return renewalPolicy && renewalPolicy.id === companyPolicyId;
    });

    if (isRenewal) {
      yield call(loadRenewalInitialState);
    } else {
      yield call(loadStandardInitialState);
    }
  });
}
/**
  Standard ER Application Initial Load
*/
function* loadStandardInitialState() {
  yield put({ type: START_INITIAL_SYNC });
  yield fork(fetchStandardInitialState);

  if (isIntuit() || isSquare()) {
    yield fork(fetchEmployees, false);
    yield fork(watchFetchEmployees);
  }
}

/**
  EmployerRenewalApplication Initial Load
*/
function* loadRenewalInitialState() {
  yield put({ type: START_INITIAL_SYNC });
  yield call(fetchRenewalInitialState);

  if (isIntuit() || isSquare()) {
    yield fork(watchFetchEmployees);
  }
}

// TODO: See if we can make this better, or skip entirely, in the ER App Initial Load
function* fetchStandardInitialState() {
  // fullSync will attempt to sync a company's employees.
  // We have a flag for intuit that blocks multiple employee syncs
  // if one was done in the last 30 minutes.
  // So if it is intuit we can always set the fullSync to true.
  const fullSync = isIntuit();
  const queryString = require("query-string");
  const parsed = queryString.parse(window.location.search);
  const companyIDToken = parsed.company_id_token || companyToken();
  const state = yield call(fetchCompanyProfileApi, fullSync, companyIDToken);

  yield put(
    getAccount({
      onSuccess: (rawResponse, successDispatch, _payload) => {
        successDispatch(receiveAccountId(rawResponse.id));
      },
    }),
  );

  yield put(replaceState(state));
  yield put({
    type: COMPANY_PROFILE_LOADED,
  });

  if (!isIntuit() && !isSquare()) {
    yield put({ type: EMPLOYEES_LOADED });
  }
}

function* fetchRenewalInitialState() {
  // fullSync will attempt to sync a company's employees.
  // We have a flag for intuit that blocks multiple employee syncs
  // if one was done in the last 30 minutes.o
  // So if it is intuit we can always set the fullSync to true.
  const fullSync = isIntuit();
  const queryString = require("query-string");
  const parsed = queryString.parse(window.location.search);
  const companyIDToken = parsed.company_id_token || companyToken();
  const state = yield call(fetchCompanyProfileApi, fullSync, companyIDToken);

  const result = yield call(init);
  state.companyProfileId = result.companyProfileId;
  state.companyPolicyId = result.renewalCompanyPolicyId;
  state.currentlyEnrolledCompanyPolicyId = result.currentlyEnrolledCompanyPolicyId;

  // we should use putResolve here instead of put (once we upgrade redux-saga to 1.x)
  // and get rid of the waitForCompanyPolicy/waitForCompanyProfile below
  yield put(
    getCompanyPolicyById(state.companyPolicyId, {
      include: ["companyContributions", "medicalPlans"],
    }),
  );

  yield put(
    getCompanyPolicyById(state.currentlyEnrolledCompanyPolicyId, {
      include: ["companyContributions", "medicalPlans"],
    }),
  );

  yield put(
    getCompanyProfileById(state.companyProfileId, {
      include: ["employeeForms"],
    }),
  );

  yield call(fetchMedicalPlans, state);
  yield call(waitForCompanyProfile, state.companyProfileId);

  yield put(requestDentalPlans());
  yield put(requestVisionPlans());

  yield put({
    type: INITIAL_STATE_LOADED,
    payload: {
      ...state,
      companyPolicyId: state.companyPolicyId,
    },
  });
  yield put({ type: COMPANY_PROFILE_LOADED });
}

// Check the store on a loop for a loaded company profile
function* waitForCompanyProfile(companyProfileId) {
  while (true) {
    const companyProfile = yield select((state) => selectCompanyProfileById(state, companyProfileId));

    if (companyProfile && Object.keys(companyProfile).length !== 0) {
      return companyProfile;
    }
    yield delay(100);
  }
}

export function* waitForCompanyPolicy() {
  while (true) {
    const companyPolicyID = yield select((state) => state.companyPolicyId);
    const companyPolicy = yield select((state) => selectCompanyPolicyById(state, companyPolicyID));

    if (companyPolicy && Object.keys(companyPolicy).length !== 0) {
      return companyPolicy;
    }
    yield delay(100);
  }
}

function* updateDentalPlans() {
  const companyPolicy = yield call(waitForCompanyPolicy);
  const response = yield call(fetchDentalPlansApi, companyPolicy);
  yield put({ type: UPDATE_DENTAL_PLANS, plans: response.results });
}

export function* RefreshDentalPlans() {
  yield takeLatest(DENTAL_PLANS_REQUESTED, updateDentalPlans);
}

function* updateVisionPlans() {
  const companyPolicy = yield call(waitForCompanyPolicy);
  const response = yield call(fetchVisionPlansApi, companyPolicy);
  yield put({ type: UPDATE_VISION_PLANS, plans: response.results });
}

export function* RefreshVisionPlans() {
  yield takeLatest(VISION_PLANS_REQUESTED, updateVisionPlans);
}

function fetchDentalPlansApi(companyPolicy) {
  return fetchWithCSRFToken(`/api/v2/company_policies/${companyPolicy.id}/dental_plans`, {
    method: "GET",
  }).then((response) => response.json());
}

function fetchVisionPlansApi(companyPolicy) {
  return fetchWithCSRFToken(`/api/v2/company_policies/${companyPolicy.id}/vision_plans`, {
    method: "GET",
  }).then((response) => response.json());
}

export function* watchFetchEmployees() {
  yield takeLatest(FETCH_EMPLOYEES, fetchEmployees, true);
}

function* fetchEmployees(forceSync) {
  const queryString = require("query-string");
  const parsed = queryString.parse(window.location.search);
  const companyIDToken = parsed.company_id_token || companyToken();
  const returnedData = yield call(fetchEmployeeApi, forceSync, companyIDToken);

  yield put(replaceEmployees(returnedData.employees));
  yield put(getCompanyApplicationByCompanyProfileId(returnedData.company_profile.id));

  yield put({ type: EMPLOYEES_LOADED });
}

export function* watchDeleteEmployee(): Generator<any, any, any> {
  yield takeEvery(DELETE_EMPLOYEE, deleteEmployee);
}

function* deleteEmployee(action): Generator<any, any, any> {
  yield put({ type: DELETING_EMPLOYEE });
  try {
    const response = yield call(deleteEmployeeApi, action.hash_id);
    yield put({ type: DELETED_EMPLOYEE, hash_id: response.hash_id });
    yield put({ type: GET_APPLICATION_MENU });
  } catch (e) {
    if (e instanceof ForbiddenError) {
      window.location.href = "/unexpected-error";
      throw e;
    }
  }
}

function deleteEmployeeApi(hash_id: string) {
  return fetchWithCSRFToken(api.deleteEmployee, {
    method: "POST",
    body: JSON.stringify({
      employee_hash_id: hash_id,
      company_id_token: companyToken(),
    }),

    processData: false,
  })
    .then(defaultHandler)
    .then((response) => response.json());
}

function fetchMedicalPlansApi(planIDGetter, relatedCartPlanIDGetter, handler) {
  return (state) => {
    // TODO: Guard code against state shouldn't have to exist, but our test library doesn't properly setup the store.
    const planIds = planIDGetter(state) || [];
    const relatedCartPlanIds = relatedCartPlanIDGetter(state) || [];
    const allIds = planIds.concat(relatedCartPlanIds).filter((v, i, a) => a.indexOf(v) === i);
    if (allIds.length === 0) return;

    const apiUrl = "/employer-application/ajax-get-medical-plans";
    const zip = get(state, "companyProfile.zip_code_number");
    const companyProfileToken = state.companyProfile.idToken;
    const planIdsStr = encodeURIComponent(JSON.stringify(allIds));
    const url = `${apiUrl}?ids=${planIdsStr}&zipcode=${zip}&company_id_token=${companyProfileToken}`;
    return fetchWithCSRFToken(url, {
      method: "GET",
      processData: false,
    })
      .then((response) => {
        if (response.status === 200) {
          return response;
        }
        return Promise.reject(response);
      })
      .then((response) => response.json())
      .then((plan) => handler(state, plan));
  };
}

export const fetchMedicalPlans = fetchMedicalPlansApi(
  (state) => get(state, "cart.medical"),
  (state) => get(state, "relatedCartPlanIds"),
  (state, response) => {
    state.allPlans = response.plans;
  },
);

export function init() {
  let url = "/employer-renewal/init";
  const companyIDToken = companyToken();

  if (companyIDToken) {
    url = `/employer-renewal/init?company_id_token=${companyIDToken}`;
  }

  return fetchWithCSRFToken(url, {
    method: "GET",
    processData: false,
  })
    .then((response) => {
      if (response.status === 401) {
        throw new Error("Logging in");
      } else if (response.status === 400) {
        return Promise.reject(response);
      } else {
        return response;
      }
    })
    .then(handleInitSuccess, fetchInitialDataFailure);
}

function handleInitSuccess(response) {
  return response.json();
}
